import React, { useState, useEffect, useCallback } from 'react';

import * as Sentry from '@sentry/nextjs';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { useAsset } from '@techstyle/react-assets';
import { Video } from '@techstyle/react-components';

import {
  trackPdpVideoPlaybackStarted,
  trackPdpVideoPlaybackCompleted,
} from '../../actions/products';
import fetchVideoThumbnail from '../../utils/fetchVideoThumbnail';
import formatVimeoThumbnailUrl from '../../utils/formatVimeoThumbnailUrl';

const VideoContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 2px;
`;

const videoWrapperStyle = css`
  width: 100%;
  height: 100%;
  flex: 0 0 auto;
  overflow: hidden;
  z-index: 1;
`;

const PlaceholderImage = styled.div`
  background-color: ${props => props.theme.colors.gray250};
  display: inline-block;
  width: 100%;
  padding-bottom: 100%;
`;

const FullImage = styled.img`
  width: 100%;
`;

const PlaceholderFullImage = styled(FullImage)``;

export const VideoCoverSizes = {
  SMALL: '300x400',
  LARGE: '600x800',
};

function ProductVideo({
  product,
  vimeoId,
  size = VideoCoverSizes.LARGE,
  aspectRatio = 3 / 4,
  ...rest
}) {
  const dispatch = useDispatch();
  const [shouldShowPlaceHolder, setShouldShowPlaceHolder] = useState(false);
  const [videoLengthInSeconds, setvideoLengthInSeconds] = useState(0);
  const [videoCoverImageUrl, setVideoCoverImageUrl] = useState(null);
  // Fixes react-player auto replaying bug for Vimeo urls
  // by setting loop prop to false when onReady event is triggered:
  // https://github.com/cookpete/react-player/issues/668
  const [isLooping, setIsLooping] = useState(true);

  const assetContainerPlaceholder = useAsset(
    'aspect_ratio_image_placeholder_container'
  );
  const { data } = assetContainerPlaceholder;
  const videoUrl = Video.getUrlFromVideoId(vimeoId);
  const assetPlaceholder = data ? data.assets[0] : null;

  // by default the thumbnail image from vimeo is a cropped horizontal rectangular image
  // getting the url of the thumbnail image from oEmbed response and resize it for our usage
  const getVimeoDetails = useCallback(async () => {
    // get vimeo o embed response with thumbnail image url
    try {
      const data = await fetchVideoThumbnail(videoUrl);

      if (data.thumbnail_url) {
        setVideoCoverImageUrl(
          formatVimeoThumbnailUrl(data.thumbnail_url, size)
        );
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [size, videoUrl]);

  useEffect(() => {
    setShouldShowPlaceHolder(false);
    getVimeoDetails();
  }, [getVimeoDetails]);

  if (!vimeoId) {
    if (!product) {
      return null;
    }

    // If 3:4 Feature is enabled it must show the 3:4 image placeholder if the image doesn't exist
    if (shouldShowPlaceHolder) {
      if (!assetPlaceholder) {
        return null;
      }
      return (
        <PlaceholderFullImage {...rest} src={assetPlaceholder.imageFilename} />
      );
    }
    return <PlaceholderImage data-testid="placeholder-image" />;
  }

  const segmentVideoDetails = {
    content_asset_id: vimeoId,
    product_id_hit: product.master_product_id,
    total_length: videoLengthInSeconds,
  };

  return (
    <VideoContainer key={vimeoId}>
      <Video
        aspectRatio={aspectRatio}
        autoPlay={false}
        data-testid="product-video"
        height="100%"
        isLooping={isLooping}
        isPlaying={false}
        light={videoCoverImageUrl || false}
        onError={() => {
          setShouldShowPlaceHolder(true);
        }}
        onProgress={() => {
          setShouldShowPlaceHolder(false);
        }}
        onReady={() => {
          setIsLooping(false);
        }}
        onDuration={duration => setvideoLengthInSeconds(duration)}
        onStart={() =>
          dispatch(trackPdpVideoPlaybackStarted(segmentVideoDetails))
        }
        onEnded={() =>
          dispatch(trackPdpVideoPlaybackCompleted(segmentVideoDetails))
        }
        onPlay={() => {
          dispatch(trackPdpVideoPlaybackStarted(segmentVideoDetails));
        }}
        playsinline
        pip={false}
        url={videoUrl}
        width="100%"
        wrapperStyle={videoWrapperStyle}
        config={{
          vimeo: {
            autoplay: false,
            controls: false,
            loop: false,
            responsive: true,
          },
          file: {
            attributes: {
              autoPlay: false,
              loop: false,
            },
          },
        }}
        {...rest}
      />
    </VideoContainer>
  );
}

ProductVideo.propTypes = {
  aspectRatio: PropTypes.number,
  /**
   * Data from the /products API for a single product. It should contain sku information.
   */
  product: PropTypes.object,
  size: PropTypes.string,
  vimeoId: PropTypes.number,
};

export default ProductVideo;
